import React, { useEffect, useState, useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import styled from "styled-components";
import { db } from "../firebase/config";
import { useAuth } from "../contexts/AuthContext";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  doc,
} from "firebase/firestore";
import { format, subDays, startOfDay } from "date-fns";
import { useTranslation } from "react-i18next";

// Register Chart.js components
ChartJS.register(
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
);

export default function Analytics({ theme }) {
  const { t } = useTranslation();
  const [chartData, setChartData] = useState(null);
  const { currentUser } = useAuth();
  const userId = currentUser?.uid;

  // REMOVED: No need for separate state management - we'll get everything from Firestore
  const [currentTaskProgress, setCurrentTaskProgress] = useState(0);
  const [currentHabitProgress, setCurrentHabitProgress] = useState(0);

  // Prepare last 7 days
  const last7Days = useMemo(() => {
    return Array.from({ length: 7 }).map((_, i) =>
      subDays(startOfDay(new Date()), 6 - i)
    );
  }, []);

  useEffect(() => {
    if (!userId) return;

    const todayKey = format(new Date(), "yyyy-MM-dd");

    // 1. Listen to TODAY'S task progress in real-time - EXACTLY like habits
    const todayTaskProgressRef = doc(
      db,
      "users",
      userId,
      "dashboardProgress",
      todayKey
    );

    const unsubscribeTodayTask = onSnapshot(
      todayTaskProgressRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const percentage = docSnap.data().percentage || 0;
          setCurrentTaskProgress(percentage);
        } else {
          // Document doesn't exist yet, set to 0
          setCurrentTaskProgress(0);
        }
      },
      (error) => {
        console.error("Error listening to task progress:", error);
      }
    );

    // 2. Listen to TODAY'S habit progress in real-time
    const todayHabitProgressRef = doc(
      db,
      "users",
      userId,
      "habitProgress",
      todayKey
    );

    const unsubscribeTodayHabit = onSnapshot(
      todayHabitProgressRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const percentage = docSnap.data().percentage || 0;
          setCurrentHabitProgress(percentage);
        } else {
          // Document doesn't exist yet, set to 0
          setCurrentHabitProgress(0);
        }
      },
      (error) => {
        console.error("Error listening to habit progress:", error);
      }
    );

    // 3. Get historical data for the chart (last 7 days) - SIMPLIFIED
    // We'll use the same approach for both tasks and habits

    const buildChart = () => {
      // Create default data for last 7 days
      const defaultData = last7Days.map((date) => {
        const dateKey = format(date, "yyyy-MM-dd");
        return {
          dateKey,
          label: format(date, "EEE"),
          taskPercentage: 0,
          habitPercentage: 0,
        };
      });

      // We'll store historical data in state
      let taskHistoryData = [];
      let habitHistoryData = [];

      // Get historical task progress (last 7 days)
      const taskQuery = query(
        collection(db, "users", userId, "dashboardProgress"),
        orderBy("date", "asc")
      );

      const unsubscribeTaskHistory = onSnapshot(
        taskQuery,
        (snapshot) => {
          taskHistoryData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

          // Update chart with task history
          updateChartData(taskHistoryData, habitHistoryData);
        },
        (error) => {
          console.error("Error loading task history:", error);
        }
      );

      // Get historical habit progress (last 7 days)
      const habitQuery = query(
        collection(db, "users", userId, "habitProgress"),
        orderBy("date", "asc")
      );

      const unsubscribeHabitHistory = onSnapshot(
        habitQuery,
        (snapshot) => {
          habitHistoryData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

          // Update chart with habit history
          updateChartData(taskHistoryData, habitHistoryData);
        },
        (error) => {
          console.error("Error loading habit history:", error);
        }
      );

      const updateChartData = (taskData, habitData) => {
        // Start with fresh default data
        const chartDataPoints = last7Days.map((date) => {
          const dateKey = format(date, "yyyy-MM-dd");
          return {
            dateKey,
            label: format(date, "EEE"),
            taskPercentage: 0,
            habitPercentage: 0,
          };
        });

        // Fill in task progress from historical data
        taskData.forEach((task) => {
          const taskDateKey = task.id; // Document ID is the date key

          // Find the index for this date
          const foundIndex = chartDataPoints.findIndex(
            (d) => d.dateKey === taskDateKey
          );

          if (foundIndex !== -1) {
            chartDataPoints[foundIndex].taskPercentage = task.percentage || 0;
          }
        });

        // Fill in habit progress from historical data
        habitData.forEach((habit) => {
          const habitDateKey = habit.id; // Document ID is the date key

          // Find the index for this date
          const foundIndex = chartDataPoints.findIndex(
            (d) => d.dateKey === habitDateKey
          );

          if (foundIndex !== -1) {
            chartDataPoints[foundIndex].habitPercentage = habit.percentage || 0;
          }
        });

        // IMPORTANT: Always use today's real-time values for today
        const todayIndex = chartDataPoints.findIndex(
          (d) => d.dateKey === todayKey
        );
        if (todayIndex !== -1) {
          chartDataPoints[todayIndex].taskPercentage = currentTaskProgress;
          chartDataPoints[todayIndex].habitPercentage = currentHabitProgress;
        }

        // Prepare chart data
        const labels = chartDataPoints.map((d) => d.label);
        const taskValues = chartDataPoints.map((d) => d.taskPercentage);
        const habitValues = chartDataPoints.map((d) => d.habitPercentage);

        const taskLineColor = "#22d3ee";
        const taskBgColor =
          theme === "dark" ? "rgba(34,211,238,0.15)" : "rgba(34,211,238,0.15)";
        const habitLineColor = "#9f62e4";
        const habitBgColor =
          theme === "dark" ? "rgba(159,98,228,0.15)" : "rgba(159,98,228,0.15)";

        setChartData({
          labels,
          datasets: [
            {
              label: t("analytics.taskCompletion"),
              data: taskValues,
              fill: true,
              borderColor: taskLineColor,
              backgroundColor: taskBgColor,
              tension: 0.4,
              pointRadius: 5,
              pointHoverRadius: 8,
              borderWidth: 2,
            },
            {
              label: t("analytics.habitCompletion"),
              data: habitValues,
              fill: false,
              borderColor: habitLineColor,
              backgroundColor: habitBgColor,
              tension: 0.4,
              pointRadius: 5,
              pointHoverRadius: 8,
              borderWidth: 2,
            },
          ],
        });
      };

      // Initial call to set up chart
      updateChartData([], []);

      return () => {
        unsubscribeTodayTask();
        unsubscribeTodayHabit();
        // Note: We can't unsubscribe here since they're inside the function
        // We'll need to restructure slightly
      };
    };

    const cleanup = buildChart();

    return cleanup;
  }, [theme, userId, last7Days, currentTaskProgress, currentHabitProgress, t]);

  // Chart options (unchanged)
  const gridColor = theme === "dark" ? "#363d52" : "#ddd";

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        min: 0,
        max: 100,
        ticks: {
          stepSize: 20,
          callback: (v) => `${v}%`,
        },
        grid: {
          color: gridColor,
          drawBorder: false,
        },
      },
      x: {
        grid: {
          color: gridColor,
          drawBorder: false,
        },
      },
    },
    plugins: {
      legend: {
        display: true,
        position: "top",
        labels: {
          padding: 20,
          usePointStyle: true,
          pointStyle: "circle",
        },
      },
      tooltip: {
        mode: "index",
        intersect: false,
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y}%`,
        },
      },
    },
    interaction: {
      intersect: false,
      mode: "index",
    },
  };

  if (!chartData) {
    return (
      <Container>
        <Title>{t("analytics.weeklyProgress")}</Title>
        <LoadingMessage>{t("analytics.loading")}</LoadingMessage>
      </Container>
    );
  }

  return (
    <Container>
      <Title>{t("analytics.weeklyProgress")}</Title>
      <ChartWrapper>
        <Line data={chartData} options={options} />
      </ChartWrapper>
      <StatsInfo>
        <Stat>
          <StatLabel>{t("analytics.currentTaskCompletion")}</StatLabel>
          <StatValue>{currentTaskProgress}%</StatValue>
        </Stat>
        <Stat>
          <StatLabel>{t("analytics.currentHabitCompletion")}</StatLabel>
          <StatValue>{currentHabitProgress}%</StatValue>
        </Stat>
      </StatsInfo>
      <SyncInfo>
        <SyncIndicator $synced={true}>
          ● {t("analytics.realTimeSync")}
        </SyncIndicator>
        <SyncNote>{t("analytics.syncNote")}</SyncNote>
      </SyncInfo>
    </Container>
  );
}

// Styled Components (unchanged)
const Container = styled.div`
  display: flex;
  flex-direction: column;
  background: var(--secondaryBackgroundColor);
  padding: 20px;
  border-radius: 20px;
  color: var(--fontPrimaryColor);
  transition: 0.5s;
`;

const Title = styled.h2`
  font-size: 20px;
  text-align: center;
  font-weight: 600;
  transition: 0.5s;
  margin: 1em 0;
`;

const ChartWrapper = styled.div`
  width: 100%;
  height: 250px;
  transition: 0.5s;
`;

const LoadingMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 250px;
  color: var(--fontSecondaryColor);
`;

const StatsInfo = styled.div`
  display: flex;
  justify-content: space-around;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid
    ${(props) => (props.theme === "dark" ? "#363d52" : "#ddd")};
`;

const Stat = styled.div`
  text-align: center;
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: var(--fontSecondaryColor);
  margin-bottom: 4px;
`;

const StatValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: var(--fontPrimaryColor);
`;

const SyncInfo = styled.div`
  margin-top: 15px;
  text-align: center;
  padding: 10px;
  background: ${(props) =>
    props.theme === "dark"
      ? "rgba(34, 211, 238, 0.1)"
      : "rgba(34, 211, 238, 0.05)"};
  border-radius: 10px;
  border: 1px solid
    ${(props) =>
      props.theme === "dark"
        ? "rgba(34, 211, 238, 0.3)"
        : "rgba(34, 211, 238, 0.2)"};
`;

const SyncIndicator = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: ${(props) => (props.$synced ? "#22d3ee" : "#f87171")};
  margin-bottom: 4px;

  &::before {
    content: "";
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${(props) => (props.$synced ? "#22d3ee" : "#f87171")};
    animation: ${(props) => (props.$synced ? "pulse 2s infinite" : "none")};
  }

  @keyframes pulse {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
    100% {
      opacity: 1;
    }
  }
`;

const SyncNote = styled.div`
  font-size: 10px;
  color: var(--fontSecondaryColor);
`;
